/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Accordion, AccordionDetails, AccordionSummary, Box, CircularProgress, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { UserRouteParameterEnum } from 'enums/userRouteParameter';
import { createForm, FormApi, Unsubscribe } from 'final-form';
import XTM from 'icons/Xtm';
import { Component, Fragment } from 'react';
import { Field, FieldRenderProps, Form } from 'react-final-form';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Dispatch } from 'redux';
import { AppDispatch, AppState } from 'store';
import { getconnectDataSpinnerSelector, getConnectToXtmSpinnerSelector } from 'store/spinner/spinner.selectors';
import { clearUserErrors, xtmConnect, xtmConnectUser, xtmGetConnectData } from 'store/user/user.actions';
import {
  getUserErrorsSelector,
  getUserSelector,
  getXtmAuthorizationIdSelector,
  getXtmConnectDataSelector,
} from 'store/user/user.selectors';
import { composeValidators, fetchValidatorObject, required } from 'utils/customValidators';
import { UserDTO } from 'utils/restApplicationClient';
import { XTMConnectionParametersDTO, XTMConnectRequest } from 'utils/restApplicationClientTypeOverrides';
import CustomField from '../../Input/CustomField';
import { customInputStyles } from '../../Input/CustomInput.styled';
import { AccordionButton, AccordionDiv, accordionStyles } from '../Accordion.style';
import AccordionIcon from '../AccordionIcon';

interface IStateProps {
  errors: { [key: string]: string | undefined };
  authorizationId?: string;
  user?: UserDTO;
  connectData?: XTMConnectionParametersDTO;
  connectDataSpinner: boolean;
  connectToXtmSpinner: boolean;
}

interface IDispatchProps {
  xtmConnect: (payload: XTMConnectRequest) => AppDispatch;
  xtmConnectUser: (payload: { userId: string; xtmParameters: XTMConnectRequest }) => AppDispatch;
  clearUserErrors: () => AppDispatch;
  getConnectData: (payload: string) => AppDispatch;
}

interface IState {
  connected: boolean;
  submitValues: XTMConnectRequest;
}

interface IMatchParams {
  id?: string;
  type?: UserRouteParameterEnum;
}

type PropType = FieldRenderProps<unknown, HTMLElement> &
  WithTranslation &
  IStateProps &
  IDispatchProps &
  RouteComponentProps<IMatchParams>;

export class XtmConnectAccordion extends Component<PropType, IState> {
  unsubscribe: Unsubscribe;
  constructor(props: PropType) {
    super(props);
    this.state = {
      connected: false,
      submitValues: {
        userUuidToCopyPassword: null,
        xtmUrl: '',
        xtmCompanyName: '',
        password: '',
        username: '',
      },
    };
    this.form = createForm({
      onSubmit: this.onSubmit,
    });
    this.unsubscribe = this.form.subscribe(
      () => {
        const { errors } = this.props;

        if (errors && errors['xtmConnect']) {
          this.props.clearUserErrors();
        }
      },
      { values: true },
    );
  }

  componentDidMount(): void {
    const {
      match: {
        params: { id: userId },
      },
      getConnectData,
    } = this.props;
    if (userId) {
      getConnectData(userId);
    }
  }

  componentDidUpdate(previousProps: PropType): void {
    const {
      authorizationId,
      errors: { xtmConnect } = {},
      connectData,
      match: {
        params: { id: userId, type },
      },
      xtmConnectUser,
    } = this.props;
    const { connected } = this.state;

    if (previousProps.authorizationId !== authorizationId && authorizationId) {
      this.props.input.onChange(authorizationId);
      this.setState({ connected: true });
    } else if (xtmConnect && connected) {
      this.props.input.onChange(undefined);
      this.setState({ connected: false });
    }

    if (previousProps.connectData === undefined && connectData && userId && type === UserRouteParameterEnum.edit) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { xtmUsername, xtmPassword, ...rest } = connectData;
      xtmConnectUser({
        userId,
        xtmParameters: {
          password: null,
          username: connectData.xtmUsername,
          userUuidToCopyPassword: userId,
          ...rest,
        },
      });
    }
  }

  componentWillUnmount(): void {
    this.unsubscribe();
  }

  form: FormApi<XTMConnectRequest, Partial<Record<string, unknown>>>;

  onSubmit = (values: XTMConnectRequest): void => {
    const {
      match: {
        params: { id: userId, type },
      },
      connectData,
    } = this.props;
    const isNewPassword = values.password !== connectData?.xtmPassword;
    if (userId) {
      const connectionParameters = {
        ...values,
        password: isNewPassword ? values.password : null,
        userUuidToCopyPassword: isNewPassword ? null : userId,
      };
      if (userId && type === UserRouteParameterEnum.edit) {
        this.props.xtmConnectUser({
          userId,
          xtmParameters: {
            ...connectionParameters,
          },
        });
      } else if (userId && type === UserRouteParameterEnum.duplicate) {
        this.props.xtmConnect({
          ...connectionParameters,
        });
      }
    } else {
      this.props.xtmConnect(values);
    }
    this.setState({ submitValues: values });
  };

  render(): JSX.Element {
    const { t, errors = {}, connectData, connectDataSpinner, connectToXtmSpinner, meta } = this.props;
    return (
      <AccordionDiv>
        <Typography component="div">
          <Box fontWeight={500} textAlign="start" width="276px" marginTop="14px" fontSize="14px" color="#333">
            {t('users.xtm.connect')}
          </Box>
        </Typography>
        <Accordion css={accordionStyles.root}>
          <AccordionSummary css={accordionStyles.summary} expandIcon={<ExpandMoreIcon />}>
            <AccordionIcon
              error={Boolean(meta.error)}
              touched={meta.touched}
              connected={this.state.connected}
              spinner={connectDataSpinner}
            />
            <span css={customInputStyles.label}>
              {this.state.connected ? t('users.connected') : t('users.disconnected')}
            </span>
          </AccordionSummary>
          <AccordionDetails css={accordionStyles.details}>
            {errors['xtmConnect'] && <span css={accordionStyles.accordionErrorText}>{t(errors['xtmConnect'])}</span>}
            <Form
              form={this.form}
              initialValues={{
                xtmUrl: connectData && connectData.xtmUrl,
                xtmCompanyName: connectData && connectData.xtmCompanyName,
                username: connectData && connectData.xtmUsername,
                password: connectData && connectData.xtmPassword,
              }}
              onSubmit={this.onSubmit}
              validate={fetchValidatorObject(
                errors['xtmConnect'],
                (this.state.submitValues as unknown) as Record<string, unknown>,
              )}
              subscription={{ pristine: true, submitting: true }}
              render={({ handleSubmit, submitting }): JSX.Element => (
                <Fragment>
                  <Field
                    name="xtmUrl"
                    component={CustomField}
                    validate={composeValidators([required])}
                    label="users.xtm.url"
                    column
                  />
                  <Field
                    name="xtmCompanyName"
                    component={CustomField}
                    validate={composeValidators([required])}
                    label="users.xtm.companyName"
                    column
                  />
                  <Field
                    name="username"
                    component={CustomField}
                    validate={composeValidators([required])}
                    label="users.xtm.user"
                    disableAutocomplete
                    column
                  />
                  <Field
                    name="password"
                    component={CustomField}
                    validate={composeValidators([required])}
                    label="users.xtm.password"
                    textFieldProps={{ type: 'password' }}
                    disableAutocomplete
                    column
                  />
                  <AccordionButton
                    variant="contained"
                    color="secondary"
                    disabled={submitting || connectToXtmSpinner}
                    onClick={handleSubmit}
                    fullWidth
                  >
                    {connectToXtmSpinner ? (
                      <CircularProgress color="secondary" size={24} />
                    ) : (
                      <Fragment>
                        <XTM /> {t('users.xtm.connect')}
                      </Fragment>
                    )}
                  </AccordionButton>
                </Fragment>
              )}
            />
          </AccordionDetails>
        </Accordion>
      </AccordionDiv>
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  errors: getUserErrorsSelector(state),
  authorizationId: getXtmAuthorizationIdSelector(state),
  user: getUserSelector(state),
  connectData: getXtmConnectDataSelector(state),
  connectDataSpinner: getconnectDataSpinnerSelector(state),
  connectToXtmSpinner: getConnectToXtmSpinnerSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  xtmConnect: (payload: XTMConnectRequest): AppDispatch => dispatch(xtmConnect(payload)),
  clearUserErrors: (): AppDispatch => dispatch(clearUserErrors()),
  getConnectData: (payload: string): AppDispatch => dispatch(xtmGetConnectData(payload)),
  xtmConnectUser: (payload: { userId: string; xtmParameters: XTMConnectRequest }): AppDispatch =>
    dispatch(xtmConnectUser(payload)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(XtmConnectAccordion)));
